/* FUNCTNL product cards: AJAX add to cart + reveal (functnl-collection). */
(() => {
  if (!('customElements' in window) || customElements.get('functnl-collection')) return;
  const reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  class FunctnlCollection extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        this._onReady = () => this.init();
        document.addEventListener('DOMContentLoaded', this._onReady, { once: true });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this._onReady) document.removeEventListener('DOMContentLoaded', this._onReady);
      this._onReady = null;
      if (this.ac) this.ac.abort();
      if (this.io) this.io.disconnect();
      clearTimeout(this.toastTimer);
      this.ac = this.io = null;
      this._ready = false;
    }

    init() {
      if (this._ready || !this.isConnected) return;
      this._ready = true;
      this.ac = new AbortController();
      const opt = { signal: this.ac.signal };
      this.toast = this.querySelector('[data-fn-toast]');
      this.toastMsg = this.querySelector('[data-fn-toast-msg]');

      this.querySelectorAll('form.fn-co__form').forEach((form) => {
        form.addEventListener('submit', (e) => this.onSubmit(e, form), opt);
      });

      this.setupReveal();
      this.setupSlider(opt);
    }

    setupSlider(opt) {
      const track = this.querySelector(':scope > .fn-co__wrap [data-co-track]');
      if (!track) return;
      const prev = this.querySelector('[data-co-prev]');
      const next = this.querySelector('[data-co-next]');
      const step = () => {
        const item = track.querySelector('.fn-co__item');
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return item ? item.getBoundingClientRect().width + gap : track.clientWidth;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const pos = Math.abs(track.scrollLeft);
        this.classList.toggle('is-static', max <= 2);
        if (prev) prev.disabled = pos <= 2;
        if (next) next.disabled = pos >= max - 2;
      };
      const move = (dir) => {
        track.scrollBy({ left: dir * step(), behavior: reduceMQ.matches ? 'auto' : 'smooth' });
        setTimeout(update, 480);
      };
      if (prev) prev.addEventListener('click', () => move(-1), opt);
      if (next) next.addEventListener('click', () => move(1), opt);
      let raf = 0;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true, signal: opt.signal });
      track.addEventListener('scrollend', update, { passive: true, signal: opt.signal });
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(update);
        ro.observe(track);
        opt.signal.addEventListener('abort', () => ro.disconnect());
      }
      update();
    }

    setupReveal() {
      const editor = !!(window.Shopify && window.Shopify.designMode);
      if (this.dataset.reveal !== 'true' || editor || reduceMQ.matches || !('IntersectionObserver' in window)) return;
      const items = this.querySelectorAll('.fn-co__item');
      if (!items.length) return;
      this.classList.add('is-reveal');
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          this.io.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
      items.forEach((el) => this.io.observe(el));
    }

    themeCart() {
      const el = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (el && typeof el.renderContents === 'function' && typeof el.getSectionsToRender === 'function') return el;
      return null;
    }

    async onSubmit(e, form) {
      const mode = this.dataset.afterAdd || 'auto';
      if (mode === 'native') return;
      e.preventDefault();
      const btn = form.querySelector('[data-fn-add]');
      const label = form.querySelector('[data-fn-label]');
      if (!btn || btn.getAttribute('aria-busy') === 'true') return;

      const original = label ? label.textContent : '';
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      if (label && this.dataset.labelAdding) label.textContent = this.dataset.labelAdding;

      const cartEl = mode === 'auto' ? this.themeCart() : null;
      const body = new FormData(form);
      if (cartEl) {
        try {
          body.append('sections', cartEl.getSectionsToRender().map((s) => s.id).join(','));
          body.append('sections_url', window.location.pathname);
          if (typeof cartEl.setActiveElement === 'function') cartEl.setActiveElement(document.activeElement);
        } catch (_) {}
      }

      try {
        const res = await fetch(`${this.dataset.addUrl || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
          credentials: 'same-origin'
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) throw new Error(data.description || data.message || this.dataset.labelError);

        if (mode === 'cart') {
          window.location.href = this.dataset.cartUrl || '/cart';
          return;
        }

        let handled = false;
        if (cartEl) {
          try {
            cartEl.classList.remove('is-empty');
            cartEl.renderContents(data);
            handled = true;
          } catch (_) {
            handled = false;
          }
        }

        this.announce(data, form);
        if (!handled) {
          const nameEl = form.closest('.fn-co__card') && form.closest('.fn-co__card').querySelector('.fn-co__name a');
          const title = (nameEl && nameEl.textContent.trim()) || data.product_title || '';
          this.showToast(`${title} ${this.dataset.labelAdded || 'added to your cart.'}`.trim(), false, true);
        }
      } catch (err) {
        this.showToast((err && err.message) || this.dataset.labelError || 'Something went wrong.', true, false);
      } finally {
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
        if (label) label.textContent = original;
      }
    }

    async announce(item, form) {
      let cart = null;
      try {
        const r = await fetch(`${(this.dataset.cartUrl || '/cart')}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (r.ok) cart = await r.json();
      } catch (_) {}
      const detail = { source: 'functnl-collection', item, cart, itemCount: cart ? cart.item_count : undefined };
      try { if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'functnl-collection', productVariantId: item && item.variant_id, cartData: cart }); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail })); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail })); } catch (_) {}
      try {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: this.id, data: { source: 'functnl-collection', itemCount: cart ? cart.item_count : 0, productId: item && item.product_id } }
        }));
      } catch (_) {}
      if (cart) {
        document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
      }
    }

    showToast(message, isError, withLink) {
      if (!this.toast || !this.toastMsg) return;
      this.toastMsg.textContent = message;
      if (withLink && !isError) {
        const a = document.createElement('a');
        a.href = this.dataset.cartUrl || '/cart';
        a.textContent = this.dataset.labelViewCart || 'View cart';
        this.toastMsg.append(' ', a);
      }
      this.toast.classList.toggle('is-error', !!isError);
      this.toast.classList.add('is-shown');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => this.toast.classList.remove('is-shown'), isError ? 5000 : 3600);
    }
  }

  customElements.define('functnl-collection', FunctnlCollection);
})();
